import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';
import { StatusBadge } from './ui/StatusBadge';
import { useToast } from './ui/Toast';
import { useApi } from '../hooks/useApi';
import type { PrometheusSession, ReleaseTrack } from '@pantheon/shared';

const select = 'border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30';

/**
 * Release rehearsal — run a Prometheus track against an Athena plan AS IT STANDS TODAY,
 * while workstreams are still in flight. Every task generates its content (GTM assets still
 * land in Drive for review) but nothing is delivered to Slack/Confluence/email, the plan is
 * not frozen, its preview stays up, and the run doesn't count as a release. The point is to
 * see what Prometheus makes and refine prompts/tracks before the real release.
 */
export function ReleaseRehearsalPanel({ initiativeId }: { initiativeId: string }) {
  const { get, post } = useApi();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tracks, setTracks] = useState<ReleaseTrack[]>([]);
  const [trackId, setTrackId] = useState('');
  const [phase, setPhase] = useState<'pre-release' | 'post-release'>('pre-release');
  const [rehearsals, setRehearsals] = useState<PrometheusSession[]>([]);
  const [busy, setBusy] = useState(false);
  const getRef = useRef(get);
  getRef.current = get;

  const load = useCallback(async () => {
    const [t, s] = await Promise.all([
      getRef.current<ReleaseTrack[]>('/prometheus/tracks'),
      getRef.current<PrometheusSession[]>('/prometheus/sessions'),
    ]);
    if (t.success && t.data) {
      setTracks(t.data);
      setTrackId((cur) => cur || t.data![0]?.trackId || '');
    }
    if (s.success && s.data) {
      setRehearsals(
        s.data
          .filter((ps) => ps.dryRun && ps.initiativeId === initiativeId)
          .sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1)),
      );
    }
  }, [initiativeId]);

  useEffect(() => { void load(); }, [load]);

  // Poll while a rehearsal is in flight so its status pill moves on its own.
  const inFlight = rehearsals.some((r) => r.status === 'running' || r.status === 'pending');
  useEffect(() => {
    if (!inFlight) return;
    const interval = setInterval(() => { void load(); }, 10_000);
    return () => clearInterval(interval);
  }, [inFlight, load]);

  const track = tracks.find((t) => t.trackId === trackId);
  const taskCount = track ? (phase === 'pre-release' ? track.preReleaseTasks : track.postReleaseTasks).length : 0;

  const rehearse = async () => {
    if (!trackId) return;
    setBusy(true);
    try {
      const result = await post<PrometheusSession>('/prometheus/sessions', { initiativeId, trackId, phase, dryRun: true });
      if (result.success && result.data) {
        toast('Rehearsal started — nothing will be sent', 'success');
        navigate(`/prometheus/${result.data.prometheusSessionId}`);
      } else {
        toast(result.error?.message ?? 'Could not start the rehearsal', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <span aria-hidden>🎭</span> Release rehearsal
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Run a Prometheus track against this plan as it stands today to see what it produces. Content is generated
            (GTM assets upload to Drive) but <strong>nothing is sent</strong>, the plan isn't frozen, and the preview stays up.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select className={select} value={trackId} onChange={(e) => setTrackId(e.target.value)} disabled={tracks.length === 0}>
          {tracks.length === 0 && <option value="">No release tracks configured</option>}
          {tracks.map((t) => <option key={t.trackId} value={t.trackId}>{t.name}</option>)}
        </select>
        <select className={select} value={phase} onChange={(e) => setPhase(e.target.value as 'pre-release' | 'post-release')}>
          <option value="pre-release">Pre-release tasks</option>
          <option value="post-release">Post-release tasks</option>
        </select>
        <Button size="sm" onClick={rehearse} loading={busy} disabled={!trackId || taskCount === 0}>
          Rehearse{track ? ` (${taskCount} task${taskCount === 1 ? '' : 's'})` : ''}
        </Button>
        {track && taskCount === 0 && (
          <span className="text-xs text-amber-700">This track has no {phase} tasks — add some in Settings → Release Tracks.</span>
        )}
      </div>

      {rehearsals.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl">
          {rehearsals.map((r) => (
            <li
              key={r.prometheusSessionId}
              className="flex items-center justify-between gap-3 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(`/prometheus/${r.prometheusSessionId}`)}
            >
              <div className="flex items-center gap-2 min-w-0">
                <StatusBadge status={r.status} />
                <span className="truncate text-gray-800">{r.trackName}</span>
                <span className="text-xs text-gray-500 shrink-0">
                  {r.tasks.filter((t) => t.status === 'completed').length}/{r.tasks.length} tasks
                </span>
              </div>
              <span className="text-xs text-gray-500 shrink-0">{new Date(r.createdAt).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
